import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Box,
  Tooltip,
  Grid
} from '@material-ui/core';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import { toast } from 'react-toastify';

// Component quản lý thời gian mưa quà
function TimeRangeManager({ timeRanges, onTimeRangesChange, settingId, onSave, loading, onSaveWithTimeRanges }) {
  // Kiểm tra props để tránh lỗi
  if (!timeRanges) {
    console.warn('TimeRangeManager: timeRanges prop is undefined or null');
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="error">
            ⚠️ Lỗi: Không có dữ liệu thời gian
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Đảm bảo timeRanges là array
  const safeTimeRanges = Array.isArray(timeRanges) ? timeRanges : [];

  const [openDialog, setOpenDialog] = useState(false);
  const [editingTimeRange, setEditingTimeRange] = useState(null);
  const [timeRangeForm, setTimeRangeForm] = useState({
    startTime: '',
    endTime: '',
    repeatType: 'DAILY',
    startDate: '',
    endDate: '',
    date: ''
  });
  const [dateValidationError, setDateValidationError] = useState('');

  // Hàm format ngày từ yyyy-mm-dd sang dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const handleAddTimeRange = () => {
    setEditingTimeRange(null);
    setTimeRangeForm({
      startTime: '',
      endTime: '',
      repeatType: 'DAILY',
      startDate: '',
      endDate: '',
      date: '',
    });
    setDateValidationError('');
    setOpenDialog(true);
  };

  const handleEditTimeRange = (timeRange) => {
    console.log('handleEditTimeRange called with:', timeRange);
    
    if (!timeRange) {
      console.error('timeRange is null or undefined');
      return;
    }
    
    // Kiểm tra cấu trúc timeRange
    if (typeof timeRange !== 'object') {
      console.error('timeRange is not an object:', typeof timeRange);
      return;
    }
    
    setEditingTimeRange(timeRange);
    setTimeRangeForm({
      startTime: timeRange.startTime || '',
      endTime: timeRange.endTime || '',
      repeatType: timeRange.repeatType || 'DAILY',
      startDate: timeRange.startDate || '',
      endDate: timeRange.endDate || '',
      date: timeRange.date || ''
    });
    setDateValidationError('');
    setOpenDialog(true);
  };

  // Validate ngày real-time
  const validateDates = (startDate, endDate) => {
    if (startDate && endDate) {
      if (new Date(startDate) >= new Date(endDate)) {
        setDateValidationError('Ngày kết thúc phải sau ngày bắt đầu');
        return false;
      } else {
        setDateValidationError('');
        return true;
      }
    }
    setDateValidationError('');
    return true;
  };

  // Handle thay đổi ngày bắt đầu
  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setTimeRangeForm(prev => ({ ...prev, startDate: newStartDate }));
    validateDates(newStartDate, timeRangeForm.endDate);
  };

  // Handle thay đổi ngày kết thúc
  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setTimeRangeForm(prev => ({ ...prev, endDate: newEndDate }));
    validateDates(timeRangeForm.startDate, newEndDate);
  };

  const handleSaveTimeRange = async () => {
    // Validation cho DATE_RANGE
    if (timeRangeForm.repeatType === 'DATE_RANGE') {
      if (!timeRangeForm.startDate || !timeRangeForm.endDate) {
        toast.warning("Vui lòng chọn đầy đủ ngày bắt đầu và ngày kết thúc");
        return;
      }
      
      if (!validateDates(timeRangeForm.startDate, timeRangeForm.endDate)) {
        toast.warning("Ngày kết thúc phải sau ngày bắt đầu");
        return;
      }
    }

    // Validation cho ONE_DAY
    if (timeRangeForm.repeatType === 'ONE_DAY') {
      if (!timeRangeForm.date) {
        toast.warning("Vui lòng chọn ngày");
        return;
      }
    }

    // Validation cho thời gian
    if (!timeRangeForm.startTime || !timeRangeForm.endTime) {
      toast.warning("Vui lòng chọn đầy đủ thời gian bắt đầu và kết thúc");
      return;
    }

    // Validation thời gian dựa trên loại lặp lại
    if (timeRangeForm.repeatType === 'DATE_RANGE') {
      // Với khoảng thời gian, so sánh cả ngày và giờ
      const startDateTime = new Date(`${timeRangeForm.startDate}T${timeRangeForm.startTime}`);
      const endDateTime = new Date(`${timeRangeForm.endDate}T${timeRangeForm.endTime}`);
      
      if (startDateTime >= endDateTime) {
        toast.warning("Thời gian kết thúc phải sau thời gian bắt đầu (bao gồm cả ngày và giờ)");
        return;
      }
    } else {
      // Với hàng ngày và một ngày, chỉ so sánh giờ
      if (timeRangeForm.startTime >= timeRangeForm.endTime) {
        toast.warning("Thời gian kết thúc phải sau thời gian bắt đầu");
        return;
      }
    }

    // Cập nhật timeRanges
    const updatedTimeRanges = [...safeTimeRanges];
    
    if (editingTimeRange) {
      // Cập nhật time range hiện có
      const index = updatedTimeRanges.findIndex(tr => tr.id === editingTimeRange.id);
      if (index !== -1) {
        updatedTimeRanges[index] = {
          ...editingTimeRange,
          ...timeRangeForm
        };
      }
    } else {
      // Thêm time range mới
      const newTimeRange = {
        id: Date.now(), // Temporary ID
        settingId: settingId,
        ...timeRangeForm
      };
      updatedTimeRanges.push(newTimeRange);
    }

    console.log('TimeRanges mới sẽ được lưu:', updatedTimeRanges);

    // Gọi API để lưu toàn bộ timeRanges
    if (onSaveWithTimeRanges) {
      try {
        // Gọi API với toàn bộ timeRanges TRƯỚC khi cập nhật UI
        await onSaveWithTimeRanges(updatedTimeRanges);
        console.log('API lưu timeRanges thành công');
        
        // Chỉ cập nhật UI sau khi API thành công
        onTimeRangesChange(updatedTimeRanges);
        toast.success(editingTimeRange ? "Cập nhật thời gian thành công!" : "Thêm thời gian thành công!");
        
        // Reset form và đóng dialog khi API thành công
        setTimeRangeForm({
          startTime: '',
          endTime: '',
          repeatType: 'DAILY',
          startDate: '',
          endDate: '',
          date: ''
        });
        setEditingTimeRange(null);
        setDateValidationError('');
        setOpenDialog(false);
        
      } catch (error) {
        console.error('Lỗi khi gọi API lưu timeRanges:', error);
        toast.error("Lỗi khi lưu thời gian mưa quà");
        
        // Nếu API lỗi, không cập nhật UI, giữ nguyên dialog
        return;
      }
    } else if (onSave) {
      try {
        // Gọi API TRƯỚC khi cập nhật UI
        await onSave();
        console.log('API lưu timeRanges thành công');
        
        // Chỉ cập nhật UI sau khi API thành công
        onTimeRangesChange(updatedTimeRanges);
        toast.success(editingTimeRange ? "Cập nhật thời gian thành công!" : "Thêm thời gian thành công!");
        
        // Reset form và đóng dialog khi API thành công
        setTimeRangeForm({
          startTime: '',
          endTime: '',
          repeatType: 'DAILY',
          startDate: '',
          endDate: '',
          date: ''
        });
        setEditingTimeRange(null);
        setDateValidationError('');
        setOpenDialog(false);
        
      } catch (error) {
        console.error('Lỗi khi gọi API lưu timeRanges:', error);
        toast.error("Lỗi khi lưu thời gian mưa quà");
        
        // Nếu API lỗi, không cập nhật UI, giữ nguyên dialog
        return;
      }
    } else {
      console.warn('onSave function không được truyền vào');
      // Nếu không có onSave, vẫn cập nhật UI
      onTimeRangesChange(updatedTimeRanges);
      
      // Reset form và đóng dialog
      setTimeRangeForm({
        startTime: '',
        endTime: '',
        repeatType: 'DAILY',
        startDate: '',
        endDate: '',
        date: ''
      });
      setEditingTimeRange(null);
      setDateValidationError('');
      setOpenDialog(false);
    }
  };

  const handleDeleteTimeRange = async (id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa thời gian này?")) {
      const originalTimeRanges = [...safeTimeRanges]; // Lưu state cũ
      const updatedTimeRanges = safeTimeRanges.filter(tr => tr.id !== id);
      
      console.log('TimeRanges gốc:', originalTimeRanges);
      console.log('TimeRanges sau khi xóa:', updatedTimeRanges);
      
      // Gọi API để lưu toàn bộ timeRanges còn lại
      if (onSaveWithTimeRanges) {
        try {
          // Gọi API với toàn bộ timeRanges còn lại TRƯỚC khi cập nhật UI
          await onSaveWithTimeRanges(updatedTimeRanges);
          console.log('API xóa timeRange thành công');
          
          // Chỉ cập nhật UI sau khi API thành công
          onTimeRangesChange(updatedTimeRanges);
          toast.success("Xóa thời gian thành công!");
        } catch (error) {
          console.error('Lỗi khi gọi API xóa timeRange:', error);
          toast.error("Lỗi khi xóa thời gian mưa quà");
          
          // Nếu API lỗi, không cập nhật UI
        }
      } else if (onSave) {
        try {
          // Gọi API TRƯỚC khi cập nhật UI
          await onSave();
          console.log('API xóa timeRange thành công');
          
          // Chỉ cập nhật UI sau khi API thành công
          onTimeRangesChange(updatedTimeRanges);
          toast.success("Xóa thời gian thành công!");
        } catch (error) {
          console.error('Lỗi khi gọi API xóa timeRange:', error);
          toast.error("Lỗi khi xóa thời gian mưa quà");
          
          // Nếu API lỗi, không cập nhật UI
        }
      } else {
        // Nếu không có onSave, vẫn cập nhật UI
        onTimeRangesChange(updatedTimeRanges);
      }
    }
  };

  const getRepeatTypeLabel = (type) => {
    switch (type) {
      case 'DAILY': return 'Hàng ngày';
      case 'DATE_RANGE': return 'Khoảng thời gian';
      case 'ONE_DAY': return 'Một ngày';
      default: return type;
    }
  };

  const getRepeatTypeColor = (type) => {
    switch (type) {
      case 'DAILY': return '#4caf50';
      case 'DATE_RANGE': return '#ff9800';
      case 'ONE_DAY': return '#2196f3';
      default: return '#666';
    }
  };

  // Hàm xác định trạng thái time range
  const getTimeRangeStatus = (timeRange) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Kiểm tra theo loại repeat
    if (timeRange.repeatType === 'DAILY') {
      // Hàng ngày - luôn đang diễn ra (vì lặp lại hàng ngày)
      return { status: 'active', label: 'Đang diễn ra', color: '#4caf50' };
    } else if (timeRange.repeatType === 'DATE_RANGE') {
      // Khoảng thời gian - chỉ kiểm tra ngày (không kiểm tra giờ)
      const startDate = new Date(timeRange.startDate);
      const endDate = new Date(timeRange.endDate);
      
      // So sánh ngày (không tính giờ)
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      if (today < startDate) {
        return { status: 'upcoming', label: 'Sắp diễn ra', color: '#ff9800' };
      } else if (today > endDate) {
        return { status: 'ended', label: 'Đã kết thúc', color: '#f44336' };
      } else {
        // Trong khoảng thời gian
        return { status: 'active', label: 'Đang diễn ra', color: '#4caf50' };
      }
    } else if (timeRange.repeatType === 'ONE_DAY') {
      // Một ngày - kiểm tra ngày cụ thể
      const targetDate = new Date(timeRange.date);
      
      // So sánh ngày (không tính giờ)
      targetDate.setHours(0, 0, 0, 0);
      
      if (today < targetDate) {
        return { status: 'upcoming', label: 'Sắp diễn ra', color: '#ff9800' };
      } else if (today > targetDate) {
        return { status: 'ended', label: 'Đã kết thúc', color: '#f44336' };
      } else {
        // Cùng ngày
        return { status: 'active', label: 'Đang diễn ra', color: '#4caf50' };
      }
    }
    
    return { status: 'unknown', label: 'Không xác định', color: '#666' };
  };

  return (
    <Card>
      <CardContent>
        <Box style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <Typography variant="h6" style={{ color: '#1976d2' }}>
            ⏰ Quản lý thời gian mưa quà
          </Typography>
          <Tooltip title="Thêm thời gian quản lý mưa quà" arrow>
            <IconButton
              onClick={handleAddTimeRange}
              style={{ 
                backgroundColor: '#1976d2',
                color: 'white',
                padding: '4px',
                borderRadius: '50%',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                marginLeft: '12px',
                '&:hover': {
                  backgroundColor: '#1565c0',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
                }
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Thời gian bắt đầu</TableCell>
                <TableCell>Thời gian kết thúc</TableCell>
                <TableCell>Loại lặp lại</TableCell>
                <TableCell>Thông tin bổ sung</TableCell>
                <TableCell align="center">Trạng thái</TableCell>
                <TableCell align="center">Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {safeTimeRanges && Array.isArray(safeTimeRanges) ? safeTimeRanges.map((timeRange) => {
                if (!timeRange || !timeRange.id) {
                  console.warn('Invalid timeRange object:', timeRange);
                  return null;
                }
                
                const status = getTimeRangeStatus(timeRange);
                return (
                  <TableRow key={timeRange.id}>
                    <TableCell>{timeRange.startTime}</TableCell>
                    <TableCell>{timeRange.endTime}</TableCell>
                    <TableCell>
                      <Chip
                        label={getRepeatTypeLabel(timeRange.repeatType)}
                        style={{
                          backgroundColor: '#f5f5f5',
                          color: '#333',
                          fontWeight: 'normal',
                          border: '1px solid #ddd'
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {timeRange.repeatType === 'DATE_RANGE' && (
                        <span>{formatDate(timeRange.startDate)} - {formatDate(timeRange.endDate)}</span>
                      )}
                      {timeRange.repeatType === 'ONE_DAY' && (
                        <span>{formatDate(timeRange.date)}</span>
                      )}
                      {timeRange.repeatType === 'DAILY' && (
                        <span style={{ color: '#666', fontStyle: 'italic' }}>Hàng ngày</span>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={status.label}
                        style={{
                          backgroundColor: status.color,
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '11px'
                        }}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditTimeRange(timeRange);
                        }}
                        style={{ color: '#2196f3' }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTimeRange(timeRange.id)}
                        style={{ color: '#f44336' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              }).filter(Boolean) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Không có dữ liệu
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      {/* Time Range Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTimeRange ? 'Chỉnh sửa thời gian' : 'Thêm thời gian mới'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: '10px' }}>
            <Grid item xs={6}>
              <TextField
                label="Thời gian bắt đầu"
                type="time"
                value={timeRangeForm.startTime}
                onChange={(e) => {
                  try {
                    const value = e.target.value;
                    if (value !== undefined) {
                      setTimeRangeForm(prev => ({ ...prev, startTime: value }));
                    }
                  } catch (error) {
                    console.error('Error in startTime onChange:', error);
                  }
                }}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  step: 60 // 1 phút
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Thời gian kết thúc"
                type="time"
                value={timeRangeForm.endTime}
                onChange={(e) => {
                  try {
                    const value = e.target.value;
                    if (value !== undefined) {
                      setTimeRangeForm(prev => ({ ...prev, endTime: value }));
                    }
                  } catch (error) {
                    console.error('Error in endTime onChange:', error);
                  }
                }}
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  step: 60 // 1 phút
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>Loại lặp lại</InputLabel>
                <Select
                  value={timeRangeForm.repeatType}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTimeRangeForm(prev => ({ ...prev, repeatType: value }));
                  }}
                  label="Loại lặp lại"
                >
                  <MenuItem value="DAILY">Hàng ngày</MenuItem>
                  <MenuItem value="DATE_RANGE">Khoảng thời gian</MenuItem>
                  <MenuItem value="ONE_DAY">Một ngày</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {timeRangeForm.repeatType === 'DATE_RANGE' && (
              <>
                <Grid item xs={6}>
                  <TextField
                    label="Ngày bắt đầu"
                    type="date"
                    value={timeRangeForm.startDate}
                    onChange={handleStartDateChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={!!dateValidationError}
                    helperText={dateValidationError ? dateValidationError : "Chọn ngày bắt đầu"}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Ngày kết thúc"
                    type="date"
                    value={timeRangeForm.endDate}
                    onChange={handleEndDateChange}
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    error={!!dateValidationError}
                    helperText={dateValidationError ? dateValidationError : "Chọn ngày kết thúc"}
                  />
                </Grid>
              </>
            )}
            {timeRangeForm.repeatType === 'ONE_DAY' && (
              <Grid item xs={12}>
                <TextField
                  label="Ngày"
                  type="date"
                  value={timeRangeForm.date}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTimeRangeForm(prev => ({ ...prev, date: value }));
                  }}
                  fullWidth
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDialog(false)} 
            startIcon={<CancelIcon />}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleSaveTimeRange} 
            color="primary" 
            startIcon={<SaveIcon />}
          >
           Xong
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default TimeRangeManager;
